import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import campService from '../services/campService';

const CampRegistration = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const campId = searchParams.get('campId');
  
  // Get user data from localStorage (set during login)
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  // Get center data from Redux
  const centerData = useSelector((state) => state.center);

  const [formData, setFormData] = useState({
    // User Information
    firstName: '',
    lastName: '',
    email: user?.email || '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    address: '',
    address2: '',
    country: 'United States',
    state: 'Georgia',
    city: '',
    zipCode: '',
    preferredContact: 'Email & Phone',
  });

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (campId) {
      fetchSessions();
    }
  }, [campId]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const data = await campService.getCampSessions(campId);
      setSessions(data || []);
    } catch (err) {
      setError('Failed to load sessions.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    try {
      setLoading(true);

      const registrationData = {
        campId: parseInt(campId),
        ...formData,
      };

      await campService.registerForCamp(registrationData);
      setSuccess(true);

      // Redirect after success
      setTimeout(() => {
        navigate('/camp/center');
      }, 2000);
    } catch (err) {
      setError('Failed to complete registration. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-indigo-50 py-16">
      <div className="container-custom max-w-2xl">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="inline-block mb-4">
            <span className="bg-gradient-to-r from-green-600 to-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
              ✓ Step 4: Registration
            </span>
          </div>
          <h1 className="text-5xl font-bold bg-gradient-to-r from-green-600 via-blue-600 to-indigo-600 bg-clip-text text-transparent mb-3">
            Complete Your Registration
          </h1>
          <p className="text-lg text-gray-600">
            Fill in your details to secure your camp spot
          </p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-green-800">
              ✓ Registration successful! Redirecting...
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Registration Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-2xl p-12 space-y-6 border-2 border-green-200">
          {/* First Name */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              First Name <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-green-600 focus:ring-2 focus:ring-green-200 transition bg-white font-medium"
              required
            />
          </div>

          {/* Last Name */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              Last Name <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-green-600 focus:ring-2 focus:ring-green-200 transition bg-white font-medium"
              required
            />
          </div>

          {/* Email Address */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              Email Address <span className="text-red-600">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 bg-gray-100"
              disabled
            />
          </div>

          {/* Mobile No */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              Mobile No <span className="text-red-600">*</span>
            </label>
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-green-600 focus:ring-2 focus:ring-green-200 transition bg-white font-medium"
              required
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              Password <span className="text-red-600">*</span>
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-green-600 focus:ring-2 focus:ring-green-200 transition bg-white font-medium"
              required
            />
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              Confirm Password <span className="text-red-600">*</span>
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-green-600 focus:ring-2 focus:ring-green-200 transition bg-white font-medium"
              required
            />
          </div>

          {/* Address */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              Address <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-green-600 focus:ring-2 focus:ring-green-200 transition bg-white font-medium"
              required
            />
          </div>

          {/* Address2 */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              Address 2
            </label>
            <input
              type="text"
              name="address2"
              value={formData.address2}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-green-600 focus:ring-2 focus:ring-green-200 transition bg-white font-medium"
            />
          </div>

          {/* Country */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Country</label>
            <select
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-green-600 focus:ring-2 focus:ring-green-200 transition bg-white font-medium"
            >
              <option value="United States">United States</option>
              <option value="Canada">Canada</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* State */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">State</label>
            <select
              name="state"
              value={formData.state}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-green-600 focus:ring-2 focus:ring-green-200 transition bg-white font-medium"
            >
              <option value="Georgia">Georgia</option>
              <option value="Florida">Florida</option>
              <option value="Texas">Texas</option>
              <option value="California">California</option>
              <option value="New York">New York</option>
            </select>
          </div>

          {/* City */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              City <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-green-600 focus:ring-2 focus:ring-green-200 transition bg-white font-medium"
              required
            />
          </div>

          {/* Zip Code */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              Zip <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              name="zipCode"
              value={formData.zipCode}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-green-600 focus:ring-2 focus:ring-green-200 transition bg-white font-medium"
              required
            />
          </div>

          {/* Preferred Contact */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Preferred Contact</label>
            <select
              name="preferredContact"
              value={formData.preferredContact}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-green-600 focus:ring-2 focus:ring-green-200 transition bg-white font-medium"
            >
              <option value="Email">Email</option>
              <option value="Phone">Phone</option>
              <option value="Email & Phone">Email & Phone</option>
            </select>
          </div>

          {/* Submit Button */}
          <div className="pt-6">
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 disabled:bg-gray-400 text-white font-bold py-4 px-6 rounded-xl transition transform hover:shadow-lg hover:scale-105"
            >
              {loading ? 'Processing...' : '✓ Submit Registration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CampRegistration;
